import { safeHref } from '@/lib/utils/url';

/**
 * 첨부파일 미리보기. 이미지는 그대로, PDF 는 iframe, 나머지는 안내 문구를 보여준다.
 *
 * 공지사항·사내지침 등 첨부를 다루는 게시판이 함께 쓴다. v1은 화면마다 같은 판정 함수를
 * 복사해 두어 어떤 화면은 webp 를 이미지로 보고 어떤 화면은 못 알아봤다.
 */
export function AttachmentPreview({
  url,
  name,
  mimeType,
  height = '420px',
  bare = false,
}: {
  url: string;
  name: string | null;
  /** 아직 업로드하지 않은 파일은 확장자 대신 MIME 으로 판단한다. */
  mimeType?: string | null;
  /**
   * 미리보기 높이. 게시판 본문 안에서는 <b>420px 로 잘라</b> 글을 계속 읽게 하지만, 증빙 뷰어처럼
   * 「이것을 보려고 연」 자리에서는 화면을 다 쓴다(수금 증빙, 2026-09-17).
   */
  height?: string;
  /**
   * 테두리·모서리를 <b>벗긴다</b>. 이미 흰 판 위에 얹히는 자리(뷰어)에서는 상자 안의 상자가 된다.
   */
  bare?: boolean;
}) {
  const kind = detectKind(url, mimeType);
  const frame = bare
    ? { height: '100%', background: '#ffffff' }
    : { border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden', background: '#ffffff' };

  if (kind === 'image') {
    return (
      <div style={{ ...frame, background: bare ? '#ffffff' : '#f8fafc' }}>
        <img
          src={url}
          alt={name ?? '첨부 이미지'}
          style={{ width: '100%', height: bare ? '100%' : undefined, maxHeight: height, objectFit: 'contain', background: '#ffffff' }}
        />
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <div style={frame}>
        <iframe src={safeHref(url)} title={name ?? '첨부 PDF'} style={{ width: '100%', height, border: 0 }} />
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: '12px',
        background: '#f8fafc',
        padding: '14px 16px',
        fontSize: '13px',
        color: 'var(--text-secondary)',
      }}
    >
      미리보기를 지원하지 않는 파일입니다. 버튼을 눌러 확인하세요.
    </div>
  );
}

function detectKind(url: string, mimeType?: string | null): 'image' | 'pdf' | 'file' {
  const mime = String(mimeType ?? '').toLowerCase();
  const lower = url.toLowerCase();

  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(lower)) return 'image';
  if (mime === 'application/pdf' || /\.pdf(\?|$)/.test(lower)) return 'pdf';
  return 'file';
}
